// src/components/TripSummary.js
import React from 'react';
import './TripSummary.css';

const TripSummary = ({ trip, places, totalCost, averageRating }) => {
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('zh-TW').format(amount);
  };

  const getTotalAmount = () => {
    return Object.values(totalCost).reduce((sum, cost) => sum + cost, 0);
  };

  const getExpensePercentage = (category) => {
    const total = getTotalAmount();
    if (total === 0) return 0;
    return ((totalCost[category] / total) * 100).toFixed(1);
  };

  const getCategoryColor = (category) => {
    const colors = {
      food: '#FF6B6B',
      accommodation: '#4ECDC4',
      transport: '#45B7D1',
      entertainment: '#96CEB4',
      other: '#FFEAA7'
    };
    return colors[category] || '#DDD';
  };

  const getCategoryName = (category) => {
    const names = {
      food: '餐飲',
      accommodation: '住宿',
      transport: '交通',
      entertainment: '娛樂',
      other: '其他'
    };
    return names[category] || category;
  };

  const renderStars = (rating) => {
    const stars = [];
    const numRating = parseFloat(rating) || 0;
    const fullStars = Math.floor(numRating);
    const hasHalfStar = numRating % 1 >= 0.5;

    // 填滿的星星
    for (let i = 0; i < fullStars; i++) {
      stars.push(<span key={i} className="star filled">★</span>);
    }

    // 半星
    if (hasHalfStar && fullStars < 5) {
      stars.push(<span key="half" className="star half">★</span>);
    }

    // 空星星
    const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);
    for (let i = 0; i < emptyStars; i++) {
      stars.push(<span key={`empty-${i}`} className="star">★</span>);
    }

    return stars;
  };

  const getDuration = () => {
    const start = new Date(trip.start_date);
    const end = new Date(trip.end_date);
    const diffTime = Math.abs(end - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    // 處理 Firebase timestamp 或 ISO 字符串
    let date;
    if (dateString.seconds) {
      // Firebase Timestamp
      date = new Date(dateString.seconds * 1000);
    } else {
      // ISO 字符串或其他格式
      date = new Date(dateString);
    }
    return date.toLocaleDateString('zh-TW');
  };

  return (
    <div className="trip-summary">
      <h3>行程摘要</h3>
      
      <div className="summary-section">
        <h4>基本資訊</h4>
        <div className="info-grid">
          <div className="info-item">
            <span className="label">目的地:</span>
            <span className="value">{trip.country} - {trip.city}</span>
          </div>
          <div className="info-item">
            <span className="label">行程天數:</span>
            <span className="value">{getDuration()} 天</span>
          </div>
          <div className="info-item">
            <span className="label">景點數量:</span>
            <span className="value">{places.length} 個</span>
          </div>
          <div className="info-item">
            <span className="label">狀態:</span>
            <span className={`value status ${trip.isCompleted ? 'completed' : 'ongoing'}`}>
              {trip.isCompleted ? '已完成' : '進行中'}
            </span>
          </div>
        </div>
      </div>

      <div className="summary-section">
        <h4>景點評分統計</h4>
        <div className="rating-summary">
          {averageRating > 0 ? (
            <>
              <div className="average-rating">
                <span className="rating-number">{averageRating}</span>
                <div className="stars">
                  {renderStars(parseFloat(averageRating))}
                </div>
              </div>
              <div className="rating-details">
                <span>{places.filter(p => Number(p.rating) > 0).length} 個景點已評分</span>
              </div>
            </>
          ) : (
            <div className="no-rating">
              <span>尚未有景點評分</span>
            </div>
          )}
        </div>
      </div>

      <div className="summary-section">
        <h4>費用統計</h4>
        <div className="cost-summary">
          <div className="total-cost">
            <span className="total-label">總花費:</span>
            <span className="total-amount">NT$ {formatCurrency(getTotalAmount())}</span>
          </div>

          {getTotalAmount() > 0 && (
            <div className="cost-breakdown">
              {Object.entries(totalCost).map(([category, amount]) => {
                if (amount === 0) return null;
                return (
                  <div key={category} className="cost-item">
                    <div className="cost-info">
                      <span 
                        className="cost-color" 
                        style={{ backgroundColor: getCategoryColor(category) }}
                      ></span>
                      <span className="cost-category">{getCategoryName(category)}</span>
                      <span className="cost-amount">NT$ {formatCurrency(amount)}</span>
                    </div>
                    <div className="cost-bar">
                      <div 
                        className="cost-fill"
                        style={{ 
                          width: `${getExpensePercentage(category)}%`,
                          backgroundColor: getCategoryColor(category)
                        }}
                      ></div>
                    </div>
                    <span className="cost-percentage">{getExpensePercentage(category)}%</span>
                  </div>
                );
              })}
            </div>
          )}

          {getTotalAmount() === 0 && (
            <div className="no-cost">
              <span>尚未記錄任何費用</span>
            </div>
          )}
        </div>
      </div>

      <div className="summary-section">
        <h4>行程狀態</h4>
        <div className="status-info">
          <div className="status-item">
            <span className="label">可見性:</span>
            <span className={`value visibility ${trip.isPublic ? 'public' : 'private'}`}>
              {trip.isPublic ? '公開' : '私人'}
            </span>
          </div>
          <div className="status-item">
            <span className="label">創建時間:</span>
            <span className="value">
              {formatDate(trip.createdAt || trip.created_at)}
            </span>
          </div>
          {trip.start_date && (
            <div className="status-item">
              <span className="label">開始日期:</span>
              <span className="value">
                {formatDate(trip.start_date)}
              </span>
            </div>
          )}
          {trip.end_date && (
            <div className="status-item">
              <span className="label">結束日期:</span>
              <span className="value">
                {formatDate(trip.end_date)}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* 景點評分分佈 */}
      {places.filter(p => Number(p.rating) > 0).length > 0 && (
        <div className="summary-section">
          <h4>景點評分分佈</h4>
          <div className="rating-distribution">
            {[5, 4, 3, 2, 1].map(rating => {
              // 確保比較時都是數字類型
              const count = places.filter(p => {
                const pRating = Number(p.rating);
                return pRating === rating && pRating > 0;
              }).length;
              
              const totalRatedPlaces = places.filter(p => {
                const pRating = Number(p.rating);
                return pRating > 0;
              }).length;
              
              const percentage = totalRatedPlaces > 0 
                ? Math.round((count / totalRatedPlaces) * 100)
                : 0;
              
              return (
                <div key={rating} className="rating-bar">
                  <span className="rating-label">{rating}星</span>
                  <div className="rating-bar-container">
                    <div 
                      className="rating-bar-fill"
                      style={{ 
                        '--target-width': `${percentage}%`,
                        width: '0%',
                        animation: count > 0 ? `fillBar 1s ease-out ${rating * 0.1}s forwards` : 'none',
                        backgroundColor: count > 0 ? '#fbbf24' : 'transparent'
                      }}
                    ></div>
                  </div>
                  <span className="rating-count">{count}</span>
                </div>
              );
            })}
            <div className="rating-distribution-summary">
              共 {places.filter(p => Number(p.rating) > 0).length} 個景點已評分
            </div>
          </div>
        </div>
      )}

      {/* 顯示即將到來的景點（如果行程尚未完成） */}
      {!trip.isCompleted && places.length > 0 && (
        <div className="summary-section">
          <h4>即將到來</h4>
          <div className="upcoming-places">
            {places
              .filter(place => new Date(place.start_time) > new Date())
              .sort((a, b) => new Date(a.start_time) - new Date(b.start_time))
              .slice(0, 3)
              .map(place => (
                <div key={place.id} className="upcoming-place">
                  <div className="place-name">{place.name}</div>
                  <div className="place-time">
                    {new Date(place.start_time).toLocaleDateString('zh-TW', {
                      month: 'short',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </div>
                </div>
              ))}
            {places.filter(place => new Date(place.start_time) > new Date()).length === 0 && (
              <div className="no-upcoming">
                <span>沒有即將到來的景點</span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default TripSummary;